import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_PREFS, loadPrefs, savePrefs } from '../storage/prefs.ts';
import { type FilterState, hasPrefParams, parseState, prefsOf, serializeState } from './state.ts';

/**
 * 第一次載入：網址有帶偏好相關參數時完全以網址為準（分享出去的連結看到的畫面才一致），否則用瀏覽器記住的偏好。
 * 上一頁／下一頁：歷史紀錄裡的網址都是這個 App 寫的（與預設相同的值會省略），所以直接照網址還原，
 * 不套用偏好；否則回到「沒有參數的首頁」時會被剛剛存下的偏好（例如已發售）蓋掉。
 */
function readState(fromHistory = false): FilterState {
  const search = window.location.search;
  return parseState(search, fromHistory || hasPrefParams(search) ? DEFAULT_PREFS : loadPrefs());
}

export type Update = Partial<FilterState> | ((s: FilterState) => FilterState);

/**
 * 篩選狀態：網址是唯一來源。
 * - push：離散的操作（勾選、排序、換頁），上一頁可以回到前一個狀態
 * - replace：連續輸入（搜尋框、日期），避免每打一個字就多一筆歷史紀錄
 */
export function useFilterState(): [FilterState, (u: Update, mode?: 'push' | 'replace') => void] {
  const [state, setState] = useState<FilterState>(readState);

  useEffect(() => {
    const onPop = () => setState(readState(true));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // 首次載入時把偏好寫回網址（讓網址反映畫面），不新增歷史紀錄
  // biome-ignore lint/correctness/useExhaustiveDependencies: 刻意只在掛載時同步一次
  useEffect(() => {
    const url = `${window.location.pathname}${serializeState(state)}${window.location.hash}`;
    if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, '', url);
    }
  }, []);

  const update = useCallback((u: Update, mode: 'push' | 'replace' = 'push') => {
    setState((prev) => {
      const next = typeof u === 'function' ? u(prev) : { ...prev, ...u };
      const url = `${window.location.pathname}${serializeState(next)}${window.location.hash}`;
      if (mode === 'push') window.history.pushState(window.history.state, '', url);
      else window.history.replaceState(window.history.state, '', url);
      savePrefs(prefsOf(next));
      return next;
    });
  }, []);

  return [state, update];
}
